import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Edit2, X, Check, ChevronRight, ChevronDown } from 'lucide-react';
import { TagInput } from './TagInput';
import { formatFinancialNumber, formatReadableNumber } from '../../../utils/numberFormatting';
import type { SimulationNode } from '../../../models/simulation';

interface HierarchicalRow {
  id: string;
  type: 'tag' | 'node';
  name: string;
  tags?: string[];
  value: number;
  baselineValue: number;
  unit?: string;
  nodeId?: string;
  level: number;
  children?: HierarchicalRow[];
  isExpanded?: boolean;
}

interface HierarchicalTableRowProps {
  row: HierarchicalRow;
  showComparison?: boolean;
  onToggle: (tagPath: string) => void;
  onNodeClick: (nodeId: string) => void;
  onNodeDeselect?: (nodeId: string) => void;
  onEditStart: (nodeId: string) => void;
  onEditCancel: () => void;
  onEditSave: (nodeId: string, tags: string[]) => void;
  isEditing: boolean;
  allNodes: SimulationNode[];
}

export const HierarchicalTableRow = ({
  row,
  showComparison = true,
  onToggle,
  onNodeClick,
  onNodeDeselect,
  onEditStart,
  onEditCancel,
  onEditSave,
  isEditing,
  allNodes
}: HierarchicalTableRowProps) => {
  const [editingTags, setEditingTags] = useState<string[]>(row.tags || []);
  const prevIsEditing = useRef(isEditing);

  const deltaAbs = row.value - row.baselineValue;
  const deltaPct = row.baselineValue === 0 ? null : (deltaAbs / Math.abs(row.baselineValue)) * 100;
  const deltaClass = deltaAbs > 0 ? 'positive' : deltaAbs < 0 ? 'negative' : 'neutral';
  const absDeltaLabel = `${deltaAbs >= 0 ? '+' : '-'}${formatFinancialNumber(Math.abs(deltaAbs))}`;
  const pctDeltaLabel =
    deltaPct === null
      ? 'n/a'
      : `${deltaPct >= 0 ? '+' : '-'}${formatReadableNumber(Math.abs(deltaPct))}%`;

  useEffect(() => {
    // Only reset editingTags when editing starts (transition from false to true)
    // Don't reset during editing when row.tags changes due to re-renders
    if (isEditing && !prevIsEditing.current) {
      // Ensure tags is always an array, never undefined
      setEditingTags(Array.isArray(row.tags) ? row.tags : []);
    }
    prevIsEditing.current = isEditing;
  }, [isEditing, row.tags]);

  const handleSave = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (row.nodeId) {
      onEditSave(row.nodeId, editingTags);
    }
  }, [row.nodeId, editingTags, onEditSave]);

  const handleCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTags(row.tags || []);
    onEditCancel();
  }, [row.tags, onEditCancel]);

  if (row.type === 'tag') {
    // Tag row (collapsible)
    return (
      <tr
        className="scenarios-table-row scenarios-table-row-tag"
        onClick={() => onToggle(row.id)}
      >
        <td className="scenarios-table-cell-deselect"></td>
        <td
          className="scenarios-table-cell-name scenarios-table-cell-tag-name"
          style={{ paddingLeft: `${row.level * 2.5 + 0.5}rem` }}
        >
          {row.level > 0 && (
            <span className="scenarios-table-connector" style={{ left: `${(row.level - 1) * 2.5 + 0.5}rem` }} />
          )}
          <span className="scenarios-table-expand-icon">
            {row.isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <span className="scenarios-table-tag-label">{row.name}</span>
          {row.children && row.children.length > 0 && (
            <span className="scenarios-table-child-count">({row.children.length})</span>
          )}
        </td>
        <td className="scenarios-table-cell-tags scenarios-table-cell-tag-summary">
        </td>
        <td className="scenarios-table-cell-value scenarios-table-cell-tag-value">
          <div className="scenarios-table-value-primary">
            <strong>{formatFinancialNumber(row.value)}</strong>
          </div>
          {showComparison ? (
            <div className={`scenarios-table-value-delta ${deltaClass}`}>
              <span>{absDeltaLabel}</span>
              <span>({pctDeltaLabel})</span>
            </div>
          ) : (
            <div className="scenarios-table-value-delta scenarios-table-value-delta-placeholder" aria-hidden="true">
              <span>&nbsp;</span>
            </div>
          )}
        </td>
        <td className="scenarios-table-cell-unit">{row.unit || '—'}</td>
      </tr>
    );
  }

  // Node row (leaf)
  return (
    <tr
      className={`scenarios-table-row scenarios-table-row-node ${isEditing ? 'scenarios-table-row-editing' : ''}`}
      onClick={() => row.nodeId && onNodeClick(row.nodeId)}
      aria-selected={isEditing}
    >
      <td className="scenarios-table-cell-deselect scenarios-table-cell-actions">
        {onNodeDeselect && row.nodeId && (
          <button
            type="button"
            className="scenarios-table-deselect-btn"
            onClick={(e) => {
              e.stopPropagation();
              onNodeDeselect(row.nodeId!);
            }}
            aria-label="Remove from selection"
            title="Remove from selection"
          >
            <X size={16} />
          </button>
        )}
      </td>
      <td
        className="scenarios-table-cell-name"
        style={{ paddingLeft: `${row.level * 2.5 + 0.5}rem` }}
      >
        {row.level > 0 && (
          <span className="scenarios-table-connector" style={{ left: `${(row.level - 1) * 2.5 + 0.5}rem` }} />
        )}
        {row.name}
      </td>
      <td className="scenarios-table-cell-tags">
        {isEditing ? (
          <div className="scenarios-table-tag-editor" onClick={(e) => e.stopPropagation()}>
            <TagInput
              value={Array.isArray(editingTags) ? editingTags : []}
              onChange={setEditingTags}
              placeholder="Add tags..."
            />
            <button
              type="button"
              className="scenarios-table-tag-editor-save"
              onClick={handleSave}
              aria-label="Save tags"
              title="Save (Cmd/Ctrl+Enter)"
            >
              <Check size={16} />
            </button>
            <button
              type="button"
              className="scenarios-table-tag-editor-close"
              onClick={handleCancel}
              aria-label="Cancel editing"
              title="Cancel (Esc)"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="scenarios-table-tags-display">
            {row.tags && row.tags.length > 0 ? (
              <div className="scenarios-table-tags-list">
                {row.tags.map((tag) => (
                  <span key={tag} className="scenarios-table-tag-badge">
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <span className="scenarios-table-no-tags">No tags</span>
            )}
            <button
              type="button"
              className="scenarios-table-edit-tags"
              onClick={(e) => {
                e.stopPropagation();
                if (row.nodeId) {
                  onEditStart(row.nodeId);
                }
              }}
              aria-label="Edit tags"
              title="Edit tags"
            >
              <Edit2 size={16} />
            </button>
          </div>
        )}
      </td>
      <td className={`scenarios-table-cell-value ${row.nodeId ? (() => {
        const node = allNodes.find(n => n.id === row.nodeId);
        const isCalculated = node?.data.type === 'formula';
        return isCalculated ? 'calculated' : 'input';
      })() : ''}`}>
        <div className="scenarios-table-value-primary">
          {formatFinancialNumber(row.value)}
        </div>
        {showComparison ? (
          <div className={`scenarios-table-value-delta ${deltaClass}`}>
            <span>{absDeltaLabel}</span>
            <span>({pctDeltaLabel})</span>
          </div>
        ) : (
          <div className="scenarios-table-value-delta scenarios-table-value-delta-placeholder" aria-hidden="true">
            <span>&nbsp;</span>
          </div>
        )}
      </td>
      <td className="scenarios-table-cell-unit">{row.unit || '—'}</td>
    </tr>
  );
};

